import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FileText, Upload, Download, Trash2, Plus, Eye, CheckCircle, XCircle, AlertTriangle, Clock, Shield, FileCheck, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BoutonPhotographier } from '../components/scan/BoutonPhotographier';
import { openBlobInNewTab, downloadBlob, base64ToBlob } from '../utils/fileUtils';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import { EnregistrerDocuments } from '../components/candidat/EnregistrerDocuments';

// Documents requis pour un stagiaire
const DOCUMENTS_REQUIS_STAGIAIRE = [
  // Documents d'inscription
  { value: 'cni_passeport', label: "Carte d'identité / Passeport", required: true },
  { value: 'psc', label: 'PSC', required: true },
  { value: 'certificat_medical', label: 'Certificat médical', required: true },
  { value: 'photo_identite', label: "Photo d'identité (format JPG)", required: true },
  { value: 'attestation_natation', label: 'Attestation natation 100m', required: true },
  { value: 'attestation_recensement', label: 'Attestation de recensement', required: false },
  { value: 'permis_cotier', label: 'Permis côtier', required: true },
  { value: 'permis_fluvial', label: 'Permis fluvial', required: true },
  { value: 'crr', label: 'CRR', required: false },
  { value: 'signature', label: 'Signature', required: true },
  { value: 'cv', label: 'CV', required: true },
];

const Documents = ({ user }) => {
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [customDocName, setCustomDocName] = useState('');
  const [uploadingType, setUploadingType] = useState(null);
  const fileInputRef = useRef(null);

  const isStagiaire = user.role === 'stagiaire' || user.role === 'moniteur_vnm';

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents/${user.user_id}`);
      setDocuments(response.data);
    } catch (error) {
    }
  };

  const handleUpload = (e, docType) => envoyerFichier(e.target.files[0], docType);

  const envoyerFichier = async (file, docType) => {
    if (!file) return;

    const effectiveType = docType || (user.role === 'centre' ? (customDocName.trim() || 'Document') : selectedType);

    setUploading(true);
    setUploadingType(docType || null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.user_id);
    formData.append('document_type', effectiveType);

    try {
      await axios.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (effectiveType === 'photo_identite' && isStagiaire) {
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const lecteur = new FileReader();
            lecteur.onload = () => resolve(lecteur.result);
            lecteur.onerror = reject;
            lecteur.readAsDataURL(file);
          });
          await axios.put(`/stagiaires/${user.user_id}`, { user_id: user.user_id, photo_identite: dataUrl });
          toast.success('Document uploadé et photo de profil mise à jour !');
        } catch {
          toast.success('Document uploadé (photo profil non mise à jour)');
        }
      } else {
        toast.success('Document uploadé avec succès');
      }

      setCustomDocName('');
      loadDocuments();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 413 && detail) {
        toast.error(detail);
      } else {
        toast.error("Erreur lors de l'upload");
      }
    } finally {
      setUploading(false);
      setUploadingType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      loadDocuments();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewDocument = async (documentId, filename) => {
    await previewDocument(documentId, filename);
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const blob = base64ToBlob(doc.file_data, doc.content_type || 'application/octet-stream');
        downloadBlob(blob, filename || doc.filename || 'document');
      }
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // For non-stagiaire roles
  const getDocumentTypes = () => {
    if (user.role === 'formateur') {
      return [
        { value: 'permis_cotier', label: 'Permis Côtier' },
        { value: 'carte_pro', label: 'Carte Pro' },
        { value: 'crr', label: 'CRR' },
        { value: 'diplome', label: 'Diplôme' },
        { value: 'photo_identite', label: "Photo d'Identité" },
        { value: 'autre', label: 'Autre' }
      ];
    }
    if (user.role === 'tuteur') {
      return [
        { value: 'diplome', label: 'Diplôme' },
        { value: 'carte_pro', label: 'Carte Pro' },
        { value: 'permis', label: 'Permis' }
      ];
    }
    if (user.role === 'responsable_pedagogique') {
      return [
        { value: 'carte_pro', label: 'Carte Professionnelle' },
        { value: 'diplome', label: 'Diplôme' },
        { value: 'permis', label: 'Permis' }
      ];
    }
    return [
      { value: 'agrement', label: 'Agrément' },
      { value: 'attestation_assurance', label: "Attestation d'Assurance" },
      { value: 'kbis', label: 'Kbis' },
      { value: 'autre', label: 'Autre' }
    ];
  };

  const documentTypes = getDocumentTypes();

  useEffect(() => {
    if (!isStagiaire && documentTypes.length > 0 && !selectedType) {
      setSelectedType(documentTypes[0].value);
    }
  }, [documentTypes, isStagiaire, selectedType]);

  // Helper to find an uploaded doc for a given type
  const getDocForType = (typeValue) => {
    return documents.find(d =>
      d.document_type === typeValue ||
      d.document_type === typeValue.replace(/_/g, ' ')
    );
  };

  // Extra documents (not in required list)
  const getExtraDocuments = () => {
    const requiredValues = DOCUMENTS_REQUIS_STAGIAIRE.map(d => d.value);
    return documents.filter(d => !requiredValues.includes(d.document_type));
  };

  // Count stats for stagiaire
  const requiredDocs = DOCUMENTS_REQUIS_STAGIAIRE.filter(d => d.required);
  const uploadedRequired = requiredDocs.filter(d => getDocForType(d.value));
  const missingRequired = requiredDocs.length - uploadedRequired.length;

  // ===== STAGIAIRE TABLE VIEW =====
  if (isStagiaire) {
    return (
      <div className="space-y-6" data-testid="documents-page">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Mes Documents</h1>
          <p className="text-slate-600">Gérez vos documents et attestations</p>
        </div>

        {/* Progress bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="documents-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Progression du dossier</span>
            <span className="text-sm font-semibold">
              {uploadedRequired.length}/{requiredDocs.length} obligatoires
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${missingRequired === 0 ? 'bg-green-500' : 'bg-[#8DC63F]'}`}
              style={{ width: `${(uploadedRequired.length / requiredDocs.length) * 100}%` }}
            ></div>
          </div>
          {missingRequired > 0 ? (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {missingRequired} document(s) obligatoire(s) manquant(s)
            </p>
          ) : (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Tous les documents obligatoires ont été fournis
            </p>
          )}
        </div>

        {/* Documents - Mobile Cards + Desktop Table */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="documents-table">
          {/* Desktop Table - hidden on mobile */}
          <table className="w-full hidden md:table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type de document</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fichier</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {DOCUMENTS_REQUIS_STAGIAIRE.map((docReq) => {
                const uploaded = getDocForType(docReq.value);
                const statut = uploaded?.statut;
                return (
                  <tr
                    key={docReq.value}
                    className={`transition-colors ${
                      uploaded ? (statut === 'refuse' ? 'bg-red-50/50' : 'bg-green-50/30') : (docReq.required ? 'bg-amber-50/30' : '')
                    }`}
                    data-testid={`doc-row-${docReq.value}`}
                  >
                    {/* Type */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{docReq.label}</span>
                        {docReq.required && <span className="text-red-400 text-xs font-bold">*</span>}
                      </div>
                    </td>

                    {/* Statut */}
                    <td className="px-4 py-3 text-center">
                      {uploaded ? (
                        statut === 'accepte' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium" data-testid={`status-${docReq.value}`}>
                            <CheckCircle className="h-3 w-3" /> Validé
                          </span>
                        ) : statut === 'refuse' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium" data-testid={`status-${docReq.value}`}>
                            <XCircle className="h-3 w-3" /> Refusé
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium" data-testid={`status-${docReq.value}`}>
                            <Clock className="h-3 w-3" /> Téléversé
                          </span>
                        )
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${
                          docReq.required ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                        }`} data-testid={`status-${docReq.value}`}>
                          {docReq.required ? <><AlertTriangle className="h-3 w-3" /> Manquant</> : 'Non fourni'}
                        </span>
                      )}
                    </td>

                    {/* Fichier */}
                    <td className="px-4 py-3">
                      {uploaded ? (
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-slate-700 truncate max-w-[200px]">{uploaded.filename}</p>
                            {uploaded.uploaded_at && (
                              <p className="text-xs text-slate-400">{new Date(uploaded.uploaded_at).toLocaleDateString('fr-FR')}</p>
                            )}
                          </div>
                          {uploaded.source === 'candidature' && (
                            <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full shrink-0">Inscription</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">-</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {uploaded && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleViewDocument(uploaded.document_id)}
                              title="Voir"
                              data-testid={`view-btn-${docReq.value}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleDownloadDocument(uploaded.document_id, uploaded.filename)}
                              title="Télécharger"
                              data-testid={`download-btn-${docReq.value}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleDeleteDocument(uploaded.document_id)}
                              title="Supprimer"
                              data-testid={`delete-btn-${docReq.value}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {docReq.value !== 'signature' && (
                          <BoutonPhotographier doc={{ id: docReq.value, nom: docReq.label }} onFichier={(f) => envoyerFichier(f, docReq.value)} disabled={uploading} className="h-7" />
                        )}
                        {/* Upload/Replace button */}
                        <div className="relative">
                          <input
                            type="file"
                            onChange={(e) => handleUpload(e, docReq.value)}
                            disabled={uploading}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                            data-testid={`upload-input-${docReq.value}`}
                          />
                          <Button
                            size="sm"
                            className={`h-7 pointer-events-none ${uploaded ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'} text-white`}
                            disabled={uploading && uploadingType === docReq.value}
                            data-testid={`upload-btn-${docReq.value}`}
                          >
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {uploading && uploadingType === docReq.value ? '...' : uploaded ? 'Remplacer' : 'Téléverser'}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-slate-100">
            {DOCUMENTS_REQUIS_STAGIAIRE.map((docReq) => {
              const uploaded = getDocForType(docReq.value);
              const statut = uploaded?.statut;
              return (
                <div
                  key={docReq.value}
                  className={`p-4 ${
                    uploaded ? (statut === 'refuse' ? 'bg-red-50/50' : 'bg-green-50/30') : (docReq.required ? 'bg-amber-50/30' : '')
                  }`}
                  data-testid={`doc-card-${docReq.value}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700">{docReq.label}</span>
                      {docReq.required && <span className="text-red-400 text-xs font-bold">*</span>}
                    </div>
                    {uploaded ? (
                      statut === 'accepte' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          <CheckCircle className="h-3 w-3" /> Valide
                        </span>
                      ) : statut === 'refuse' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                          <XCircle className="h-3 w-3" /> Refuse
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                          <Clock className="h-3 w-3" /> Televerse
                        </span>
                      )
                    ) : (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${
                        docReq.required ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {docReq.required ? <><AlertTriangle className="h-3 w-3" /> Manquant</> : 'Non fourni'}
                      </span>
                    )}
                  </div>

                  {uploaded && (
                    <p className="text-xs text-slate-500 truncate mb-2">{uploaded.filename}</p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    {uploaded && (
                      <>
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleViewDocument(uploaded.document_id)}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Voir
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleDownloadDocument(uploaded.document_id, uploaded.filename)}>
                          <Download className="h-3.5 w-3.5 mr-1" /> Telecharger
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(uploaded.document_id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {docReq.value !== 'signature' && (
                      <BoutonPhotographier doc={{ id: docReq.value, nom: docReq.label }} onFichier={(f) => envoyerFichier(f, docReq.value)} disabled={uploading} suffixe="-mobile" className="h-8" />
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        onChange={(e) => handleUpload(e, docReq.value)}
                        disabled={uploading}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Button size="sm" className={`h-8 text-xs pointer-events-none ${uploaded ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'} text-white`}>
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        {uploaded ? 'Remplacer' : 'Televerser'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Extra documents (uploaded under types not in the required list) */}
        {getExtraDocuments().length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="extra-documents">
            <h2 className="text-lg font-display font-semibold text-[#0F172A] mb-3">Autres documents téléversés</h2>
            <div className="space-y-2">
              {getExtraDocuments().map((doc) => (
                <div key={doc.document_id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-[#8DC63F]" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">{doc.filename}</p>
                      <p className="text-xs text-slate-500 capitalize">{doc.document_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDocument(doc.document_id)}><Eye className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDocument(doc.document_id, doc.filename)}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(doc.document_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload custom/autre document */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="upload-autre-section">
          <h2 className="text-lg font-display font-semibold text-[#0F172A] mb-3">Ajouter un autre document</h2>
          <div className="flex flex-col md:flex-row gap-3 items-start">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-other-type"
            >
              <option value="autre">Autre document</option>
              {DOCUMENTS_REQUIS_STAGIAIRE.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <div className="relative">
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleUpload(e, selectedType || 'autre')}
                disabled={uploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                data-testid="file-input-other"
              />
              <Button
                type="button"
                disabled={uploading}
                className="bg-[#8DC63F] hover:bg-[#7db336] text-white pointer-events-none"
                data-testid="upload-other-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Upload en cours...' : 'Sélectionner le fichier'}
              </Button>
            </div>
          </div>
        </div>

        <EnregistrerDocuments
          nbEnAttente={documents.filter(d => d.envoye_centre === false).length}
          endpoint="/documents/enregistrer"
          onEnvoye={loadDocuments}
        />

        <p className="text-xs text-slate-400 text-center">* Documents obligatoires</p>
        <DocumentModal />
      </div>
    );
  }

  // ===== NON-STAGIAIRE VIEW (original layout) =====
  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Documents</h1>
        <p className="text-slate-600">Gérez vos documents et attestations</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Uploader un Document</h2>

        {user.role === 'centre' ? (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                value={customDocName}
                onChange={(e) => setCustomDocName(e.target.value)}
                placeholder="Nom du document (ex: Convention de stage, Règlement intérieur...)"
                className="flex-1 h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="custom-doc-name"
              />
              <div className="relative">
                <input
                  type="file"
                  onChange={(e) => handleUpload(e)}
                  disabled={uploading || !customDocName.trim()}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  data-testid="file-input"
                />
                <Button
                  type="button"
                  disabled={uploading || !customDocName.trim()}
                  className="bg-[#8DC63F] hover:bg-[#7db336] text-white pointer-events-none"
                  data-testid="upload-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {uploading ? 'Upload en cours...' : 'Ajouter le document'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">Écrivez le nom du document puis cliquez sur "Ajouter le document" pour sélectionner un fichier</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col md:flex-row gap-4">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="flex-1 h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="select-document-type"
              >
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <div className="relative">
                <input
                  type="file"
                  onChange={(e) => handleUpload(e)}
                  disabled={uploading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  data-testid="file-input"
                />
                <Button
                  type="button"
                  disabled={uploading}
                  className="bg-[#8DC63F] hover:bg-[#7db336] text-white pointer-events-none"
                  data-testid="upload-button"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Upload en cours...' : 'Sélectionner le fichier'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents List */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Mes Documents</h2>
        <div className="space-y-3">
          {documents.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Aucun document uploadé</p>
          ) : (
            documents.map((doc) => (
              <div
                key={doc.document_id}
                className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors"
                data-testid={`document-item-${doc.document_id}`}
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
                    <FileText className="h-5 w-5 text-[#8DC63F]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#0F172A]">{doc.filename}</p>
                      {doc.source === 'candidature' && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Inscription</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 capitalize">
                      {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type?.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleViewDocument(doc.document_id)} title="Voir" data-testid={`view-button-${doc.document_id}`}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownloadDocument(doc.document_id, doc.filename)} title="Télécharger" data-testid={`download-button-${doc.document_id}`}>
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDeleteDocument(doc.document_id)} className="text-red-600 border-red-200 hover:bg-red-50" title="Supprimer" data-testid={`delete-button-${doc.document_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <DocumentModal />
    </div>
  );
};

export default Documents;
